<template>
  <div>
    <a-icon v-if="submitting" class="spinner" type="loading" />
    <form id="subscriptionForm" :class="{ 'submitting': submitting }" @submit.prevent='saveSubscription'>
      <h1>Subscription</h1>

      <div class='form-row'>
        <label>Plan</label>
        <Dropdown v-model="form.plan" width='100%'>
          <a-select-option title="Trial" value="trial">Trial</a-select-option>
          <a-select-option title="Customer" value="customer">Customer</a-select-option>
        </Dropdown>
      </div>

      <div class='form-row' :class="{ 'form-group--error': $v.form.companyName.$error }">
        <label>Company name</label>
        <CompanyAutocomplete :onBlur="onBlur" v-model="$v.form.companyId.$model" />
        <div class="error" v-if="!$v.form.companyName.required && $v.form.companyName.$error">Required</div>
      </div>

      <div v-if="form.plan =='customer'" class='form-row' :class="{ 'form-group--error': $v.form.companyRegistrationId.$error }">
        <label>Company number</label>
        <input type='text' placeholder="Registered company number" v-model="$v.form.companyRegistrationId.$model" />
      </div>

      <div class='form-row'>
        <label>Location</label>

        <span style="width: 100%;">
          <SearchableDropdown v-model="form.country" width='100%' placeholder="Find country">
            <a-select-option v-for="(country, index) in countries" :key="index" :value="country.value">{{ country.label }}</a-select-option>
          </SearchableDropdown>
        </span>

        <span style="position:relative;margin-left:10px;" :class="{ 'form-group--error': $v.form.city.$error }">
          <input :disabled="!form.country" type='text' placeholder="Enter city name" v-model="$v.form.city.$model" />
          <div class="error" v-if="!$v.form.city.required && $v.form.city.$error">Required</div>
        </span>
      </div>

      <div class='form-row' :class="{ 'form-group--error': $v.form.expiresAt.$error }">
        <label>Expiry date</label>
        <a-date-picker v-model='$v.form.expiresAt.$model' placeholder='Expiry date' class='end-date' />
        <div class="error" v-if="!$v.form.expiresAt.required && $v.form.expiresAt.$error">Required</div>
      </div>

      <div class='form-row' v-for="(user, index) in $v.form.users.$each.$iter" :key="index">
        <label>User {{ +index + 1 }} <span class="delete" @click='() => confirmRemoveUser(index)'><img alt="Delete" src="../../assets/delete.png"></span></label>
        
        <div class="static-email" v-if="!user.new.$model">{{ user.email.$model }}</div>

        <span v-if="user.new.$model" style="position:relative;margin-right:10px;width:100%;" :class="{ 'form-group--error': user.email.$error }">
          <input style="width:100%;" type='text' placeholder="Email" v-model="user.email.$model" />
          <div class="error" v-if="!user.email.required && user.email.$error">Required</div>
        </span>

        <span style="position: relative" :class="{ 'form-group--error': user.password.$error }">
          <input type='text' :placeholder="user.new.$model ? 'Choose password' : 'Reset password'" v-model="user.password.$model" />
          <div class="error" v-if="!user.password.required && user.password.$error">Required</div>
        </span>

      </div>

      <div class='form-row no-label'>
        <a @click="addNewUser">+ Add another user</a>
      </div>

      <div class='form-row button-footer'>
        <button class='flat-button' key="back" @click.prevent="close">Cancel</button>
        <button class='raised-button' key="submit" :disabled='$v.form.$invalid || submitting'>{{ form.id ? 'Update' : 'Create' }} subscription</button>
      </div>
    </form>
  </div>

</template>

<script>
  import Dropdown from '@/components/Dropdown'
  import SearchableDropdown from '@/components/SearchableDropdown'
  import CompanyAutocomplete from '@/components/subscriptions/CompanyAutocomplete'
  import countries from '@/constants/countries'
  import nouns from '@/constants/nouns'
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
  import { isEqual, debounce, sample } from 'lodash'
  import { required, requiredIf } from 'vuelidate/lib/validators'

  export default  {
    name: 'NewSubscriptionForm',
    components: {
      Dropdown,
      SearchableDropdown,
      CompanyAutocomplete
    },
    data() {
      return {
        countries,
        nouns,
        form: {}
      }
    },
    validations: {
      form: {
        companyId: { required },
        companyName: { required },
        companyRegistrationId: { 
          required: requiredIf(function(form) {
            return this.form.plan === 'customer' && this.form.country === 'GB'
          })
        },
        city: { 
          required: requiredIf(function(form) {
            return this.form.country ? true : false
          })
        },
        expiresAt: { required },
        users: {
          $each: {
            email: {
              required
            },
            password: {
              required: requiredIf(function(user) {
                return user.new ? true : false
              })
            },
            new: {}
          }
        }
      }
    },
    computed: {
      ...mapState('newSubscriptionForm', { vuexForm: 'form', submitting: 'submitting' }),
      companyId() {
        return this.form.companyId
      },
      country() {
        return this.form.country
      }
    },
    methods: {
      ...mapMutations('newSubscriptionForm', ['update', 'close', 'addUser']),
      ...mapActions('newSubscriptionForm', ['createSubscription', 'updateSubscription', 'deleteLicense']),
      ...mapGetters('newSubscriptionForm', ['getCompany']),
      addNewUser() { 
        const password = this.generatePassword()
        this.addUser(password) 
      },
      removeUser(index) {
        const user = this.form.users[index]
        const subscriptionId = this.form.id

        this.deleteLicense({ user, subscriptionId })
        this.form.users.splice(index, 1)
        this.form.subscriptionHolders = this.form.users.map(u => u.email).join(',')
      },
      confirmRemoveUser(index) {
        var confirm = window.confirm("Are you sure you want to permanently delete this user from the subscription?")
        if(confirm) this.removeUser(index)
      },
      saveSubscription() {
        this.form.id ? this.updateSubscription(this.form) : this.createSubscription(this.form)
      },
      randomNumber() { return Math.floor(Math.random() * 899 + 100) },
      generatePassword() {
        return `peg${ this.randomNumber() }${ sample(nouns) }`
      },
      onBlur() {
        this.$v.form.companyName.$touch();
      }
    },
    watch: {
      form: {
        handler: debounce(function(updatedForm) {
          this.update(updatedForm)
        }, 300), deep: true
      },
      companyId(id) {
        const company = this.getCompany()(id)
        this.form.companyName = company.name
        this.form.companyRegistrationId = company.registrationId
      },
      country(code) {
        if(code === '') this.form.city = ''
      }
    },
    beforeMount() {
      if(!this.vuexForm.users.length) {
        const password = this.generatePassword()
        this.addUser(password)
      }
      this.form = this.vuexForm
    }
  }
</script>

<style scoped lang="stylus">
  @import '../styles/buttons'
  @import '../styles/forms'
  
  .delete {
    cursor: pointer;
    img {
      width: 11px;
      margin-left: 5px;
    }
    &:hover {
      opacity: 0.5;
    }
  }
  
  .static-email {
    width: 50%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 10px;
  }
</style>